/**
 * Flow state for the `(setup)` route group (ADR 0006).
 *
 * The onboarding steps used to be six branches of one `MultiStepModal`, so the
 * answers collected along the way could live in that single component. They are
 * real routes now, and each screen unmounts as the user moves to the next one,
 * so the payload, the current step, and the in-flight/error status have to live
 * above them.
 *
 * `SetupProvider` is mounted by `(setup)/_layout.tsx`: the state therefore
 * survives navigation within the group and dies with it, which is exactly the
 * lifetime the flow wants. It WRAPS `useOnboarding()` rather than replacing it —
 * `onboarding` is still a real concept (the gateway guided experience), and the
 * hook remains its single owner of server reads and writes.
 */

import { useQueryClient } from '@tanstack/react-query';
import { router, type Href } from 'expo-router';
import * as React from 'react';

import type { EligibleExperiencesResult } from '@/api/endpoints/guided-experiences';
import { GUIDED_EXPERIENCE_QUERY_KEYS } from '@/api/queries/guided-experiences';
import { useOnboarding } from '@/features/onboarding/hooks/use-onboarding';
import type { OnboardingIntent, OnboardingPayload } from '@/features/onboarding/types';
import { useAuth } from '@/hooks/use-auth';

/** The gateway guided experience these screens drive. */
const ONBOARDING_EXPERIENCE_ID = 'onboarding';

/**
 * Step N is `SETUP_STEP_ROUTES[N - 1]`. The `username` screen is deliberately
 * absent: it is the auth-owned account-setup claim, not one of onboarding's six
 * steps, so "step 3 of 6" means the same thing for both cohorts.
 */
export const SETUP_STEP_ROUTES = [
  '/welcome',
  '/permissions',
  '/intent',
  '/taste',
  '/friends',
  '/done',
] as const;

const FIRST_STEP = 1;
const LAST_STEP = SETUP_STEP_ROUTES.length;

export interface SetupContextValue {
  payload: OnboardingPayload;
  /** `null` until the eligibility read resolves and the step is actually known. */
  currentStep: number | null;
  isAdvancing: boolean;
  error: Error | null;
  setIntent: (intent: OnboardingIntent) => void;
  skipFriendSeeding: () => void;
  advance: () => Promise<void>;
  goBack: () => void;
  complete: () => Promise<void>;
}

const SetupContext = React.createContext<SetupContextValue | null>(null);

export function useSetupContext(): SetupContextValue {
  const value = React.useContext(SetupContext);
  if (value === null) {
    throw new Error(
      'useSetupContext must be used inside a SetupProvider. Setup screens are mounted by (setup)/_layout.tsx, which provides it.'
    );
  }
  return value;
}

function toError(cause: unknown): Error {
  return cause instanceof Error ? cause : new Error(String(cause));
}

function routeForStep(step: number): Href {
  return SETUP_STEP_ROUTES[step - 1];
}

export function SetupProvider({ children }: { children: React.ReactNode }) {
  const onboarding = useOnboarding();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id ?? '';

  const [error, setError] = React.useState<Error | null>(null);
  const hasStartedRef = React.useRef(false);

  const {
    status,
    start,
    nextStep,
    goBack: stepBack,
    complete: completeExperience,
    currentStep: onboardingStep,
    payload,
    isAdvancing,
    setIntent,
    skipFriendSeeding,
  } = onboarding;

  // Claiming the server-side experience record is what makes `current_step`
  // resumable. It runs once per mounted group, not once per screen.
  React.useEffect(() => {
    if (status !== 'required' || hasStartedRef.current) return;
    hasStartedRef.current = true;
    start().catch((cause: unknown) => setError(toError(cause)));
  }, [status, start]);

  const currentStep = status === 'loading' ? null : onboardingStep;

  const advance = React.useCallback(async () => {
    setError(null);
    if (onboardingStep >= LAST_STEP) return;
    try {
      await nextStep();
    } catch (cause) {
      setError(toError(cause));
      return;
    }
    router.push(routeForStep(onboardingStep + 1));
  }, [nextStep, onboardingStep]);

  const goBack = React.useCallback(() => {
    setError(null);
    if (onboardingStep <= FIRST_STEP) return;
    stepBack();
    // A resumed session can land directly on a mid-flow route with nothing
    // behind it, in which case there is no entry to pop.
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.replace(routeForStep(onboardingStep - 1));
  }, [onboardingStep, stepBack]);

  const complete = React.useCallback(async () => {
    setError(null);
    try {
      await completeExperience();
    } catch (cause) {
      // Stay put. Navigating on a failed completion parks the user on a screen
      // whose only action already failed.
      setError(toError(cause));
      return;
    }

    // Drop the entry from the cached list so the app gate releases in this same
    // tick, then reconcile against the server in the background.
    const eligibleKey = GUIDED_EXPERIENCE_QUERY_KEYS.eligible(userId);
    queryClient.setQueryData<EligibleExperiencesResult>(eligibleKey, (previous) =>
      previous === undefined
        ? previous
        : {
            ...previous,
            experiences: previous.experiences.filter(
              (experience) => experience.experience_id !== ONBOARDING_EXPERIENCE_ID
            ),
          }
    );
    queryClient.invalidateQueries({ queryKey: eligibleKey });
  }, [completeExperience, queryClient, userId]);

  const value = React.useMemo<SetupContextValue>(
    () => ({
      payload,
      currentStep,
      isAdvancing,
      error,
      setIntent,
      skipFriendSeeding,
      advance,
      goBack,
      complete,
    }),
    [
      payload,
      currentStep,
      isAdvancing,
      error,
      setIntent,
      skipFriendSeeding,
      advance,
      goBack,
      complete,
    ]
  );

  return <SetupContext.Provider value={value}>{children}</SetupContext.Provider>;
}
